import DescriptionIcon from '@material-ui/icons/Description';
import { Typography } from '@material-ui/core';
import LanguageIcon from '@material-ui/icons/Language';
import LinkIcon from '@material-ui/icons/Link';
import QueryBuilderIcon from '@material-ui/icons/QueryBuilder';
import ToggleOnIcon from '@material-ui/icons/ToggleOn';
import cloneDeep from 'lodash/cloneDeep';
import PropTypes from 'prop-types';
import React from 'react';
import { APPLICATION_PATHS } from '../application-core/constants';
import BasicLanguageField from '../core/BasicLanguageField';
import ResourceLink from '../core/ResourceLink';
import TitledPanel from '../core/TitledPanel';
import TypedLiteralDialog from '../core/TypedLiteralDialog';
import FindResourceDialog from '../ontology/FindResourceDialog';
import BooleanField from './BooleanField';
import DateField from './DateField';
import DatetimeField from './DatetimeField';
import GetBooleanValueDialog from './GetBooleanValueDialog';
import GetDatatypeValueDialog from './GetDatatypeValueDialog';
import GetDatetimeValueDialog from './GetDatetimeValueDialog';
import GetDateValueDialog from './GetDateValueDialog';
import LangConfirmationField from './LangConfirmationField';
import LiteralConfirmationField from './LiteralConfirmationField';
import Notification from './Notification';
import { DATATYPES } from './utils/constants';
import { computeDatatype, sortResources } from './utils/utils';


class DatatypeDisplay extends React.Component {

  // For now assuming only ontologies will use this
  buildLink = (resourceData) => {
    const ontology = resourceData.visibility || resourceData.isDefinedBy;
    if (!ontology) return null;
    let view = "class";
    if (resourceData.type.includes("ObjectProperty")) {
      view = "objectProperty";
    }
    else if (resourceData.type.includes("DatatypeProperty")) {
      view = "datatypeProperty";
    }
    else if (resourceData.type.includes("AnnotationProperty")) {
      view = "annotationProperty";
    }

    let link = APPLICATION_PATHS.ONTOLOGY_MANAGER + '/project/ontology#project=' +
      this.props.projectIRI + "&ontology=" + ontology + "&view=" + view + "&" + view + "=" + resourceData.id;
    return link;
  };

  render() {

    const { isReadOnly, title, datatype, values, onAddLiteral, onAddTypedLiteral, defaultLang, projectLang, deprecated, info, graph,
      onDelete, onUpdate, property, source, associatedProjectGraph, viewLang, addNewResource, context, noRangeOptions,
      canAddValues, cardinalityAtMax, exceedsCardinality, searchClasses, components } = this.props;

    let dt = datatype;
    if (dt === "http://www.w3.org/2000/01/rdf-schema#Literal") dt = DATATYPES.STRING;

    let indexedValues = cloneDeep(values).map((value, index) => ({
      index,
      data: value
    }));

    // Although this is a datatype component we are flexible enough to handle no range so we process IRIs too
    let existingResourceValues = values.filter(value => value.id);
    let existingResourceIRIs = existingResourceValues.map(value => value.id);

    // We need the existing langstring values in a form we can feed into BasicLanguageField
    const existingLangstringValues = {};
    values
      .filter(value => value.language)
      .forEach(value => Array.isArray(existingLangstringValues[value.language]) ?
        existingLangstringValues[value.language].push(value["@value"])
        :
        existingLangstringValues[value.language] = [value["@value"]]
      );

    // For other values we simply extract a value
    const existingValues = values
      .filter(value => !value.language && !value.id)
      .map(value => value["@value"] || value);

    // Separate to the ordered indexed values we need datatypes for typed literals
    // We exclude booleans as done separately (will have true/false values)
    const existingTypedLiteralValues = values
      .filter(value => !value.language && !value.id && !(typeof value === "boolean"))
      .map(content => {
        const dt = computeDatatype(content);
        const tc = typeof content === "object";
        const text = tc ?
          content["@value"]
          :
          content;
        return {
          text,
          datatype: tc ?
            content["type"]
            :
            dt || DATATYPES.STRING
        };
      });

    // We need to extract out non langstring fields
    const orderedValues = indexedValues
      .sort((a, b) => {
        const aValue = a.data["@value"] || a.data;
        const bValue = b.data["@value"] || b.data;
        if (aValue.toString().toLowerCase() < bValue.toString().toLowerCase()) return -1;
        if (aValue.toString().toLowerCase() > bValue.toString().toLowerCase()) return 1;
        const aType = a.data.type || DATATYPES.STRING;
        const bType = b.data.type || DATATYPES.STRING;
        if (aType < bType) return -1;
        if (aType > bType) return 1;
        return 0;
      });

    let dialog = null;
    let knownDatatype = true;

    if (!isReadOnly && !info.patternConflict && !cardinalityAtMax && canAddValues && !deprecated && !(dt === DATATYPES.BOOLEAN && values.length > 0)) {
      switch (dt) {
        case "":
          // If no range then we give the user the option to select from a resource or one of our supported datatypes
          dialog = [
            <FindResourceDialog
              key={viewLang}
              source={source}
              defaultLang={defaultLang}
              components={components}
              searchClass={searchClasses}
              includeSystemGraph={false}
              title={title}
              permitExternal={true}
              graph={graph}
              icon={<LinkIcon />}
              viewLang={viewLang}
              exclude={existingResourceIRIs || []}
              onAddResource={addNewResource} />
          ];
          if (noRangeOptions.boolean) {
            dialog.push(<GetBooleanValueDialog
              key="boolean-dialog"
              title={"Add a new boolean value"}
              disabled={(existingValues && existingValues.length > 0) || (existingResourceValues && existingResourceValues.length > 0)}
              icon={<ToggleOnIcon />}
              name={title}
              onAddLiteral={onAddTypedLiteral} />);
          }
          if (noRangeOptions.language) {
            dialog.push(<BasicLanguageField
              key="langstring-dialog"
              dataTest="langstring"
              icon={<LanguageIcon />}
              enforceLanguage={true}
              existingValues={existingLangstringValues}
              multiline={false}
              maxLength={info.maxLength || 1000}
              defaultLang={defaultLang}
              langs={projectLang}
              title={"Add a Value"}
              onAddLanguage={onAddLiteral} />);
          }
          if (noRangeOptions.dateTime) {
            dialog.push(<GetDatetimeValueDialog
              key="datetime-dialog"
              title={"Add " + title}
              icon={<QueryBuilderIcon />}
              name={title}
              existingValues={existingValues}
              onSetDate={onAddTypedLiteral} />);
          }
          if (noRangeOptions.datatype) {
            dialog.push(<TypedLiteralDialog
              key="literal-dialog"
              dataTest="add-typed-literal"
              onAddLiteral={onAddTypedLiteral}
              existingValues={existingTypedLiteralValues}
              excludeDatatypes={noRangeOptions.excludeDatatypes}
              title="Add Value"
              placeholder="Enter text"
              name={title} />);
          }
          break;
        case DATATYPES.LANGSTRING:
          dialog = <BasicLanguageField
            dataTest="langstring"
            existingValues={existingLangstringValues}
            multiline={false}
            excludeNoLanguage={true}
            maxLength={info.maxLength || 1000}
            defaultLang={defaultLang}
            langs={projectLang}
            title={"Add " + title}
            onAddLanguage={onAddLiteral} />;
          break;
        case DATATYPES.DATE:
          dialog = <GetDateValueDialog
            title={"Add " + title}
            name={title}
            existingValues={existingValues}
            onSetDate={onAddTypedLiteral} />;
          break;
        case DATATYPES.DATETIME:
          dialog = <GetDatetimeValueDialog
            title={"Add " + title}
            name={title}
            existingValues={existingValues}
            onSetDate={onAddTypedLiteral} />;
          break;
        case DATATYPES.BOOLEAN:
          dialog = <GetBooleanValueDialog title={"Add " + title} name={title} onAddLiteral={onAddTypedLiteral} />;
          break;
        case DATATYPES.STRING: case DATATYPES.ANYURI:
          dialog = <GetDatatypeValueDialog
            title={"Add " + title}
            maxLength={info.maxLength || 1000}
            pattern={info.pattern}
            name={title}
            datatype={dt}
            existingValues={existingValues}
            onAddLiteral={onAddTypedLiteral} />;
          break;
        case DATATYPES.INTEGER: case DATATYPES.DECIMAL: case DATATYPES.FLOAT: case DATATYPES.DOUBLE: case DATATYPES.LONG:
          dialog = <GetDatatypeValueDialog
            title={"Add " + title}
            name={title}
            datatype={dt}
            existingValues={existingValues}
            onAddLiteral={onAddTypedLiteral} />;
          break;
        default:
          dialog = null;
          knownDatatype = false;
      }
    }

    let existingResourceValuesIDs = existingResourceValues.map(value => value.id);
    let sortedExistingResourceValues = sortResources(existingResourceValuesIDs, graph, "label", defaultLang);
    let resourceFields = sortedExistingResourceValues.map(id => {
      return <ResourceLink
        key={id}
        id={id}
        source={source}
        property={property}
        projectGraph={graph}
        context={context}
        ignoreNotAvailable={true}
        onDeleteLink={() => onDelete(property, null, null, graph)(id)}
        contextProperty="isDefinedBy"
        graph={graph}
        link={this.buildLink}
        isReadOnly={isReadOnly}
        viewLang={viewLang}
        handleNotAvailable={true}
        defaultLang={defaultLang} />;
    });

    // First we display any langstring fields
    let langstringFields = null;
    langstringFields = orderedValues
      .filter(value => value.data.language)
      .sort((a, b) => {
        if (a.data.language < b.data.language) return -1;
        if (a.data.language > b.data.language) return 1;
        if (a.data["@value"].toLowerCase() < b.data["@value"].toLowerCase()) return -1;
        if (a.data["@value"].toLowerCase() > b.data["@value"].toLowerCase()) return 1;
        return 0;
      })
      .map((value) => {
        const lang = value.data.language;
        return <LangConfirmationField
          dataTest="langstring"
          maxLength={info.maxLength || 1000}
          key={lang + "-" + value.data["@value"]}
          onUpdate={onUpdate(property, lang, value.index, associatedProjectGraph)}
          onDelete={onDelete(property, lang, null, associatedProjectGraph)}
          existingValues={values
            .filter(existingValue => existingValue.language === lang)
            .map(existingValue => existingValue["@value"] || existingValue)}
          canBeDeleted={true}
          invalidDatatype={dt !== DATATYPES.LANGSTRING && dt !== ""}
          text={value.data["@value"]}
          lang={lang}
          isReadOnly={isReadOnly} />;
      });

    // When sorting we need to be careful to retain the original index position in the array for updates
    return <TitledPanel
      warning={
        exceedsCardinality ?
          "Too many values. Only " + (info.maxCount || 1) + " expected."
          :
          ""}
      dataTest={"property-" + title}
      height="full"
      actions={dialog}
      title={info.name || title}>
      {info.description &&
        <div style={{ marginBottom: 12 }}>
          <Typography variant="body2"><DescriptionIcon style={{ transform: 'translateY(4px)' }} fontSize="small" /> <span data-test="description">{info.description}</span></Typography>
        </div>
      }
      {info.minCount && orderedValues.length < info.minCount && <Notification dataTest="warning" error={false} text={"Min. values required: " + info.minCount} />}
      {info.patternConflict && <Notification dataTest="pattern-conflict" error={false} text={"Patterns from multiple templates are conflicting. Please fix."} />}
      {info.minCountConflict && <Notification dataTest="min-count-conflict" error={false} text={"Min. Count values from multiple templates are conflicting. The largest value is being used."} />}
      {info.maxCountConflict && <Notification dataTest="max-count-conflict" error={false} text={"Max. Count values from multiple templates are conflicting. The smallest value is being used."} />}
      {info.maxLengthConflict && <Notification dataTest="max-length-conflict" error={false} text={"Max. Length values from multiple templates are conflicting. The smallest value is being used."} />}
      {!knownDatatype && <Notification error={false} text="This property has an unsupported range." dataTest="unsupported" />}
      {deprecated && <Notification error={false} text="This property is deprecated." dataTest="deprecated" />}
      {!canAddValues &&
        <Notification error={false} text="This resource no longer has a class valid for this property." dataTest="not-valid" />
      }
      {resourceFields}
      {langstringFields}
      {orderedValues &&
        orderedValues
          .filter(value => !value.data.language && !value.data.id)
          .map(value => {
            // We need to consider the fact that the range of the property may conflict with the actual value
            // We display the value datatype as is but show warning
            let typeToCheck = dt;
            let valueText = value.data["@value"] || value.data;
            if (value.data.type) {
              typeToCheck = value.data.type;
            }
            else {
              if (typeof valueText === "boolean" && dt !== DATATYPES.BOOLEAN) typeToCheck = DATATYPES.BOOLEAN;
              else if (typeof valueText === "number") typeToCheck = DATATYPES.INTEGER;
              else if (typeof valueText === "string" && dt !== DATATYPES.STRING) typeToCheck = DATATYPES.STRING;
            }
            switch (typeToCheck) {
              case DATATYPES.DATE:
                return <DateField
                  invalidDatatype={dt !== typeToCheck && dt !== ""}
                  key={valueText}
                  isReadOnly={isReadOnly}
                  date={valueText}
                  existingValues={existingValues}
                  onDelete={onDelete(property, null, typeToCheck, associatedProjectGraph)}
                  onUpdate={onUpdate(property, null, value.index, associatedProjectGraph)} />;
              case DATATYPES.DATETIME:
                return <DatetimeField
                  invalidDatatype={dt !== typeToCheck && dt !== ""}
                  key={valueText}
                  isReadOnly={isReadOnly}
                  dateTime={valueText}
                  existingValues={existingValues}
                  onDelete={onDelete(property, null, typeToCheck, associatedProjectGraph)}
                  onUpdate={onUpdate(property, null, value.index, associatedProjectGraph)} />;
              case DATATYPES.BOOLEAN:
                return <BooleanField
                  key={"boolean-" + valueText}
                  invalidDatatype={dt !== typeToCheck && dt !== ""}
                  isReadOnly={isReadOnly}
                  checked={typeof valueText === "boolean" ? valueText : valueText === "true"}
                  onDelete={onDelete(property, null, typeToCheck, associatedProjectGraph)}
                  onUpdate={onUpdate(property, null, value.index, associatedProjectGraph)} />;
              case DATATYPES.STRING: case DATATYPES.ANYURI:
                return <LiteralConfirmationField
                  dataTest={title}
                  key={typeToCheck + "-" + valueText}
                  margin="dense"
                  text={valueText}
                  maxLength={info.maxLength || 1000}
                  pattern={info.pattern}
                  invalidDatatype={dt !== typeToCheck && dt !== ""}
                  datatype={typeToCheck}
                  existingValues={existingTypedLiteralValues}
                  canBeDeleted={true}
                  onDelete={onDelete(property, null, typeToCheck, associatedProjectGraph)}
                  onUpdate={onUpdate(property, null, value.index, associatedProjectGraph)}
                  isReadOnly={isReadOnly} />;
              case DATATYPES.INTEGER: case DATATYPES.DECIMAL: case DATATYPES.FLOAT: case DATATYPES.DOUBLE: case DATATYPES.LONG:
                return <LiteralConfirmationField
                  dataTest={title}
                  key={typeToCheck + "-" + valueText}
                  margin="dense"
                  text={valueText.toString()}
                  datatype={typeToCheck}
                  invalidDatatype={dt !== typeToCheck && dt !== ""}
                  existingValues={existingTypedLiteralValues}
                  canBeDeleted={true}
                  onDelete={onDelete(property, null, typeToCheck, associatedProjectGraph)}
                  onUpdate={onUpdate(property, null, value.index, associatedProjectGraph)}
                  isReadOnly={isReadOnly}
                />;
              default: return null;
            }
          })}
    </TitledPanel>;
  }
}

DatatypeDisplay.defaultProps = {
  datatype: DATATYPES.STRING,
  cardinalityAtMax: false,
  canAddValues: true,
  info: {},
  searchClasses: [],
  noRangeOptions: {
    boolean: true,
    language: true,
    dateTime: true,
    datatype: true,
    excludeDatatypes: []
  }
};

DatatypeDisplay.propTypes = {
  canAddValues: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  deprecated: PropTypes.bool,
  source: PropTypes.string,
  info: PropTypes.object,
  context: PropTypes.string,
  cardinalityAtMax: PropTypes.bool,
  exceedsCardinality: PropTypes.bool,
  datatype: PropTypes.string,
  components: PropTypes.array,
  onAddLiteral: PropTypes.func.isRequired,
  projectIRI: PropTypes.string,
  noRangeOptions: PropTypes.object,
  onAddTypedLiteral: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  viewLang: PropTypes.string,
  searchClasses: PropTypes.array,
  projectLang: PropTypes.array.isRequired,
  graph: PropTypes.string,
  defaultLang: PropTypes.string.isRequired,
  property: PropTypes.string.isRequired,
  associatedProjectGraph: PropTypes.string.isRequired,
  onDelete: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  values: PropTypes.array.isRequired,
  addNewResoure: PropTypes.func
};

export default DatatypeDisplay;